import { ChangeDetectionStrategy, Component, EventEmitter, inject, input, Input, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CreationMode, e2eLocators } from '@components/storage-accounts/add-storage-account-wizard/add-storage-account-wizard.constants';
import { CredentialFormAzureVmComponent } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/azure-vm/credential-form-azure-vm.component';
import { CredentialFormAzureComponent } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/azure/credential-form-azure.component';
import { CredentialFormBackblazeComponent } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/backblaze/credential-form-backblaze.component';
import { StorageFormName } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/credential-forms.constants';
import { CredentialFormFileSystemComponent } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/file-system/credential-form-file-system.component';
import { CredentialFormFtpComponent } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/ftp/credential-form-ftp.component';
import { CredentialFormGoogleCloudComponent } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/google-cloud/credential-form-google-cloud.component';
import { CredentialFormGoogleDriveComponent } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/google-drive/credential-form-google-drive.component';
import { CredentialFormIDriveE2Component } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/idrive-e2/credential-form-idrive-e2.component';
import { CredentialFormImpossibleCloudComponent } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/impossible-cloud/credential-form-impossible-cloud.component';
import { CredentialFormOpenstackComponent } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/openstack/credential-form-openstack.component';
import { CredentialFormS3CompatibleComponent } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/s3-compatible/credential-form-s3-compatible.component';
import { CredentialFormS3Component } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/s3/credential-form-s3.component';
import { CredentialFormSftpComponent } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/sftp/credential-form-sftp.component';
import { CredentialFormWasabiComponent } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/wasabi/credential-form-wasabi.component';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  CardComponent,
  CardGroupComponent,
  CardHeaderComponent,
  GroupButtonComponent,
  InputComponent,
  LoaderComponent,
  MbsPopupType,
  TableCellDirective,
  TableGridComponent,
  TableHeader,
  TextEllipsisComponent
} from 'mbs-ui-kit';
import { DateTimeFormatComponent } from 'mbs-ui-shared@components/date-time-format/date-time-format.component';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import { StorageType } from 'mbs-ui-shared@models';
import { StorageAccountV2ListUnit } from 'mbs-ui-shared@models/StorageAccount';
import { StorageDisplayNamePipe } from 'mbs-ui-shared@utils/pipes/storage-display-name.pipe';
import { StorageTypeIconPipe } from 'mbs-ui-shared@utils/pipes/storage-type-icon.pipe';

@Component({
  selector: 'mbs-storage-account-step',
  templateUrl: './storage-account-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertComponent,
    CardComponent,
    CardGroupComponent,
    CardHeaderComponent,
    CredentialFormAzureComponent,
    CredentialFormAzureVmComponent,
    CredentialFormBackblazeComponent,
    CredentialFormFileSystemComponent,
    CredentialFormFtpComponent,
    CredentialFormGoogleCloudComponent,
    CredentialFormGoogleDriveComponent,
    CredentialFormIDriveE2Component,
    CredentialFormOpenstackComponent,
    CredentialFormS3CompatibleComponent,
    CredentialFormS3Component,
    CredentialFormSftpComponent,
    I18NextPipe,
    InputComponent,
    LoaderComponent,
    ReactiveFormsModule,
    CredentialFormWasabiComponent,
    StorageDisplayNamePipe,
    StorageTypeIconPipe,
    HelpLinkDirective,
    CredentialFormImpossibleCloudComponent,
    ButtonComponent,
    GroupButtonComponent,
    TableGridComponent,
    TableCellDirective,
    DateTimeFormatComponent,
    TextEllipsisComponent
  ]
})
export class StorageAccountStepComponent {
  private i18n = inject(I18NextService);

  @Input() storageType: StorageType;
  @Input() form: FormGroup;
  @Input() accountDisplayName: FormControl;
  @Input() error: string;
  @Input() mode: CreationMode;
  @Input() isNativeMultipartControlShown: boolean;
  @Input() existingAccounts: StorageAccountV2ListUnit[] = [];
  @Input() useExistingControl: FormControl;
  @Input() storageAccountControl: FormControl;
  @Input() existingLoading = false;
  @Input() existingLoaded = false;
  @Input() canSelectSource = false;
  @Input() selectedAccountId: string;
  @Output() loadMoreExisting = new EventEmitter<void>();
  helpLinkValue = input<string>();

  public readonly StorageType = StorageType;
  public readonly MbsPopupType = MbsPopupType;
  public readonly CreationMode = CreationMode;
  public readonly e2eLocators = e2eLocators;
  public readonly infiniteScrollDistance = 10;
  public readonly infiniteScrollThrottle = 50;

  // Only the existing account list has to wait for a response, the credentials form can be shown right away
  public get sourceReady(): boolean {
    return !this.useExistingControl?.value || this.existingLoaded;
  }

  public readonly existingAccountsHeaders: TableHeader[] = [
    {
      name: this.i18n.t('storages:addStorageAccountWizard.inputCredentialsStepSource.columns.name'),
      gridColSize: '2fr',
      gridColMin: '80px',
      overflow: true
    },
    {
      name: this.i18n.t('storages:addStorageAccountWizard.inputCredentialsStepSource.columns.accountId'),
      gridColSize: '2fr',
      gridColMin: '80px',
      overflow: true,
      info: this.i18n.t('storages:accountIdDescription')
    },
    {
      name: this.i18n.t('storages:addStorageAccountWizard.inputCredentialsStepSource.columns.created'),
      gridColSize: '2fr',
      gridColMin: '160px'
    }
  ];

  public getSelectedExistingAccounts(): StorageAccountV2ListUnit[] {
    const selected = this.existingAccounts?.find(({ id }) => id === this.selectedAccountId);

    return selected ? [selected] : [];
  }

  public onExistingAccountSelected(selected: StorageAccountV2ListUnit[]): void {
    this.storageAccountControl.setValue(selected?.[0] ?? null);
  }

  public getForm(storageType: StorageType): FormGroup {
    return this.form.get(StorageFormName[storageType]) as FormGroup;
  }
}
